"use client";

import { RoleGate } from "@/components/gate/RoleGate";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { useUserGroupsData } from "@/data/use-groups-data";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAnglesUpDown,
  faIndustry,
  faShield,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { Link, useNavigate } from "@tanstack/react-router";
import { useGroupSlug } from "../../../hooks/use-group-slug";
import { cn } from "../../../lib/utils";
import Logo from "../../Logo/Logo";
import { groupListLabel } from "../../display/GroupDisplayName";
import { BRAND_NAME } from "../consts";

export function SidebarGroupSwitcher() {
  const { isMobile, open } = useSidebar();
  const { groups } = useUserGroupsData();
  const groupSlug = useGroupSlug(false);
  const navigate = useNavigate();

  const activeGroup = groups.find((w) => w.slug === groupSlug);

  const handleGroupChange = (groupSlug: string) => {
    navigate({ to: "/w/$groupSlug", params: { groupSlug } });
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className={cn(
                "data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground",
                { "rounded-full": !open },
              )}
            >
              <div className="text-sidebar-primary-foreground flex aspect-square size-20 items-center justify-center rounded-lg">
                <Logo size={100} />
              </div>
              <div className="grid flex-1 text-left text-sm leading-tight">
                {/* @TODO replace with org name */}
                <span className="truncate font-semibold">{BRAND_NAME}</span>
                <span className="truncate text-xs">
                  {activeGroup ? groupListLabel(activeGroup) : null}{" "}
                  <span className="text-muted-foreground">
                    {activeGroup?.organizationName}
                  </span>
                </span>
              </div>
              <FontAwesomeIcon icon={faAnglesUpDown} className="ml-auto" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            align="start"
            side={isMobile ? "bottom" : "right"}
            sideOffset={4}
          >
            <RoleGate groupRoles={["admin", "creator"]} systemRoles={["super"]}>
              <div className="flex flex-col">
                <DropdownMenuLabel className="text-muted-foreground text-xs">
                  Workspace Settings
                </DropdownMenuLabel>
                <DropdownMenuItem asChild className="cursor-pointer">
                  <Link
                    to="/w/$groupSlug/settings"
                    params={{ groupSlug: groupSlug! }}
                  >
                    <FontAwesomeIcon icon={faShield} /> Workspace Settings
                  </Link>
                </DropdownMenuItem>
              </div>
            </RoleGate>
            <DropdownMenuLabel className="text-muted-foreground text-xs">
              Groups
            </DropdownMenuLabel>
            {[...groups]
              .sort((left, right) => {
                if (left.personal !== right.personal) {
                  return left.personal ? -1 : 1;
                }
                return left.name.localeCompare(right.name);
              })
              .map((group, index) => (
                <DropdownMenuItem
                  key={group.uuid}
                  onClick={() => handleGroupChange(group.slug)}
                  className={cn("cursor-pointer gap-2 p-2", {
                    "font-bold": group.slug === groupSlug,
                    "cursor-default": group.slug === groupSlug,
                  })}
                >
                  <div className="flex size-6 items-center justify-center rounded-sm border">
                    <FontAwesomeIcon icon={faIndustry} />
                  </div>
                  <div className="grid min-w-0 flex-1 text-left leading-tight">
                    <span className="truncate">{groupListLabel(group)}</span>
                    <span className="text-muted-foreground truncate text-xs font-normal">
                      {group.organizationName}
                    </span>
                  </div>
                  <DropdownMenuShortcut>⌘{index + 1}</DropdownMenuShortcut>
                </DropdownMenuItem>
              ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
